<template>
    <info-modal ref="groupSelectionModal" v-model:is-open="isOpen" size="large" :title="t('groupSelection.title')" @did-close="emit('did-close')">
        <template #body>
            <div class="flex min-h-0 flex-1 flex-col gap-3">
                <div :class="['flex-1', GRID_LAYOUT_CLASSES]">
                    <template v-if="groupsWithContext.highlightedGroup">
                        <h1 v-if="groupsWithContext.highlightedGroupLabel" class="col-span-full text-start">
                            {{ groupsWithContext.highlightedGroupLabel }}
                        </h1>
                        <radio-button
                            :id="groupsWithContext.highlightedGroup._id"
                            v-model="selectedGroupId"
                            name="group"
                            :value="groupsWithContext.highlightedGroup._id"
                            :label="groupsWithContext.highlightedGroup.name"
                            class="col-span-1"
                        />
                        <hr class="col-span-full w-full border border-neutral-300" />
                    </template>
                </div>
                <div :class="['min-h-0 flex-1 overflow-y-auto', GRID_LAYOUT_CLASSES]">
                    <radio-button
                        v-for="group in groupsWithContext.remainingGroups"
                        :id="group._id"
                        :key="group._id"
                        v-model="selectedGroupId"
                        name="group"
                        :value="group._id"
                        :label="group.name"
                        class="min-w-52"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex w-full justify-end gap-4">
                <button class="rounded-lg border-2 border-neutral-300 px-5 py-2.5 text-center text-sm font-medium dark:text-neutral-100" @click="isOpen = false">{{ t("cancel") }}</button>
                <button
                    :disabled="!canSubmit"
                    class="rounded-lg bg-primary px-5 py-2.5 text-center text-sm font-medium text-neutral-100 disabled:border-neutral-300 disabled:bg-neutral-300 disabled:text-neutral-400"
                    @click="submit"
                >
                    {{ t("confirm") }}
                </button>
            </div>
        </template>
    </info-modal>
</template>

<script setup lang="ts">
    import InfoModal from "@/components/modals/InfoModal.vue";
    import RadioButton from "@/components/RadioButton.vue";
    import { BaseGroupDto, RecordedAssignmentWithEmployeeNameDto } from "@/services/api/services";
    import { computed, ref, useTemplateRef, watch } from "vue";
    import { useI18n } from "vue-i18n";
    import { useGroupService } from "@/services/group.service";
    import { toast } from "vue3-toastify";

    const SECONDS_BEFORE_AUTO_DISMISS: number = 30;
    const GRID_LAYOUT_CLASSES = "grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3";

    const { t } = useI18n({ useScope: "global" });
    const groupService = useGroupService();

    const isOpen = defineModel("isOpen", { type: Boolean, required: true });
    const props = defineProps<{
        recordedAssignment: RecordedAssignmentWithEmployeeNameDto;
        pin: string;
        groups: BaseGroupDto[];
    }>();
    const emit = defineEmits<{
        "did-change-group": [updatedRecordedAssignment: RecordedAssignmentWithEmployeeNameDto];
        "did-close": [];
    }>();

    const selectedGroupId = ref<string>();
    const mainGroupOfEmployee = ref<BaseGroupDto | null>(null);

    const canSubmit = computed(() => Boolean(selectedGroupId.value));
    const currentlyAssignedGroupId = computed(() => props.recordedAssignment?.group);

    // The highlighted group is either the currently assigned group or the employee's main group, shown separately above the rest
    const groupsWithContext = computed((): { highlightedGroup: BaseGroupDto | null; highlightedGroupLabel: string | null; remainingGroups: BaseGroupDto[] } => {
        if (currentlyAssignedGroupId.value) {
            return {
                highlightedGroup: props.groups.find((g) => g._id === currentlyAssignedGroupId.value) ?? null,
                highlightedGroupLabel: t("groupSelection.assignedGroupLabel"),
                remainingGroups: props.groups.filter((g) => g._id !== currentlyAssignedGroupId.value),
            };
        }

        if (mainGroupOfEmployee.value) {
            return {
                highlightedGroup: mainGroupOfEmployee.value,
                highlightedGroupLabel: t("groupSelection.mainGroupLabel"),
                remainingGroups: props.groups.filter((g) => g._id !== mainGroupOfEmployee.value!._id),
            };
        }

        return { highlightedGroup: null, highlightedGroupLabel: null, remainingGroups: props.groups };
    });

    const groupSelectionModal = useTemplateRef("groupSelectionModal");
    watch(isOpen, async (newValue) => {
        if (newValue) {
            try {
                mainGroupOfEmployee.value = await groupService.getMainGroupForEmployee(props.pin);
            } catch (error) {
                console.error("An error occurred while trying to fetch the employee's main group", JSON.stringify(error));
                toast.error(t("groupSelection.errors.fetchMainGroup"));
            }

            groupSelectionModal.value?.setAutoDismissAfter({ seconds: SECONDS_BEFORE_AUTO_DISMISS });
        }
    });

    watch(groupsWithContext, (newGroups) => {
        selectedGroupId.value = newGroups.highlightedGroup?._id ?? undefined;
    });

    async function submit() {
        groupSelectionModal.value?.stopAutoDismiss();

        if (!selectedGroupId.value) {
            return;
        }

        try {
            if (!props.recordedAssignment?._id) {
                throw new Error("Missing ID");
            }

            const updatedRecordedAssignment = await groupService.changeGroupForRecordedAssignment(props.pin, props.recordedAssignment._id, selectedGroupId.value);
            if (updatedRecordedAssignment) {
                emit("did-change-group", updatedRecordedAssignment);
            }
        } catch (error) {
            console.error("Error while trying to update recorded assignment", JSON.stringify(error));
            toast.error(t("groupSelection.errors.changeGroup"));
            groupSelectionModal.value?.setAutoDismissAfter({ seconds: SECONDS_BEFORE_AUTO_DISMISS });
        }
    }
</script>
